import { Check, ExternalLink, Mail } from "lucide-react";

import { type PortalCatalogEntry, portalLaunchUrl, useAppsCatalog } from "@/api/apps";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * ATTMOS Apps section for the Settings page (M9.5 Phase E / #347).
 *
 * Lists every portal the signed-in session is **allowed to know about**
 * (the `/api/apps/catalog` endpoint filters out
 * `listed_in_catalog: false` portals for non-entitled users) with the
 * entitlement state:
 *
 * - **Entitled** → "Open" link (same `target="_blank"` + `noopener,noreferrer`
 *   pattern as the launcher).
 * - **Not entitled** → "Request access" mailto link pointing at the
 *   portal's `owner_contact.email`.
 *
 * Distinct from the `/apps` launcher route which renders only the
 * entitled subset and is action-oriented; the Settings panel is
 * informational + onboarding-flow ("how do I get access to X?").
 */
export function EntitlementPanel(): JSX.Element {
  const { data: catalog, isLoading, isError, error } = useAppsCatalog();

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground">
        Could not load ATTMOS apps: {error instanceof Error ? error.message : "unknown error"}.
      </p>
    );
  }

  if (!catalog || catalog.length === 0) {
    // Per Rupert PR-357 r0 #2 + #6: don't hardcode a roster of
    // product names (leaks the full internal portal set to a user
    // who is by definition entitled to none of them — undermines
    // the catalog's existence-confidentiality contract); don't
    // assert "not configured" (an empty catalog can also mean
    // every configured app is unlisted/confidential to this user).
    return (
      <p className="text-sm text-muted-foreground">
        No ATTMOS apps available to you. Contact a FlowStack admin if you expected to see apps here.
      </p>
    );
  }

  const entitledCount = catalog.filter((p) => p.entitled).length;
  const hasNonEntitled = entitledCount < catalog.length;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        You are entitled to {entitledCount} of {catalog.length} ATTMOS apps.
        {/* Per Rupert PR-357 r0 #3 + #7: only mention the request-access
            affordance when at least one non-entitled row will render it;
            and avoid "Cognito group" — it leaks an IdP-specific detail
            in user-facing copy. */}
        {hasNonEntitled
          ? ' Use the "Request access" link below to ask the owning team to grant you access.'
          : ""}
      </p>
      <ul className="flex flex-col gap-2">
        {catalog.map((portal) => (
          <PortalRow key={portal.slug} portal={portal} />
        ))}
      </ul>
    </div>
  );
}

function PortalRow({ portal }: { portal: PortalCatalogEntry }): JSX.Element {
  return (
    <li
      className={cn(
        "flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2",
        portal.entitled ? "bg-surface-2" : "bg-card"
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{portal.display_name}</span>
          <EntitlementBadge entitled={portal.entitled} />
        </div>
        {portal.tagline ? (
          <p className="line-clamp-1 text-xs text-muted-foreground">{portal.tagline}</p>
        ) : (
          <p className="font-mono text-[11px] text-muted-foreground">{portal.embed.hostname}</p>
        )}
      </div>
      <PortalAction portal={portal} />
    </li>
  );
}

function EntitlementBadge({ entitled }: { entitled: boolean }): JSX.Element {
  if (entitled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-cyan/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-cyan">
        <Check className="h-3 w-3" aria-hidden />
        Entitled
      </span>
    );
  }
  return (
    <span className="label-micro inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
      Not entitled
    </span>
  );
}

function PortalAction({ portal }: { portal: PortalCatalogEntry }): JSX.Element | null {
  if (portal.entitled) {
    const url = portalLaunchUrl(portal);
    // Same defense-in-depth gate as the launcher card — if the
    // client-side URL validator refuses the manifest, skip the
    // affordance rather than render a non-functional link.
    if (url === null) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex shrink-0 items-center gap-1 rounded-md border border-cyan/30 px-2 py-1 text-xs font-medium text-cyan",
          "hover:bg-cyan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        )}
        aria-label={`Open ${portal.display_name} in a new tab`}
      >
        <ExternalLink className="h-3 w-3" aria-hidden />
        Open
      </a>
    );
  }
  const subject = encodeURIComponent(`FlowStack: request access to ${portal.display_name}`);
  // Per Rupert PR-357 r0 #5: owner_contact.email is interpolated raw
  // here, which is safe because the backend ``PortalManifest`` schema
  // validates it via Pydantic's ``EmailStr`` (RFC 5322 shape +
  // reasonable char-set restrictions) — a manifest containing
  // mailto-breaking characters in the address would fail validation
  // at server-startup load time and never reach the SPA. Subject
  // line is properly ``encodeURIComponent``'d above because it's
  // free-text derived from the display_name.
  return (
    <a
      href={`mailto:${portal.owner_contact.email}?subject=${subject}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs font-medium text-muted-foreground",
        "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      aria-label={`Email ${portal.owner_contact.team} to request access to ${portal.display_name}`}
    >
      <Mail className="h-3 w-3" aria-hidden />
      Request access
    </a>
  );
}
